'use client';

import clsx from 'clsx';
import { useCallback, useEffect, useMemo } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useGetVipDetail } from '@/features/user-vip/services/vip';
import { Link } from '@/i18n/navigation';
import { useRouter } from '@/i18n/navigation';
import { track } from '@/utils/track';
import { toFormat } from '@/libs/decimal';
import { toNumFixed } from '@/utils/currency';
import IconFont from '@/components/ui/iconFont';
import { levelMap } from '@/features/user-vip/constant';
import { getVipCardThemeData } from '@/features/user-vip/themeData';
import Progress from '@/features/user-vip/components/progress';
import vip1MaskImg from '@/features/user-vip/images/vip/vip1-mask.png';
import useVipUpgrade from '../../hooks/useVipUpgrade';

interface UserLevelProps {
  className?: string;
  onBeforeLinkJump?: () => void;
  onVipDataLoad?: () => void;
}

export default function UserLevel({ className = '', onBeforeLinkJump, onVipDataLoad }: UserLevelProps) {
  const { data: vipDetail, isLoading, refetch } = useGetVipDetail.useQueryHook({});
  const t = useTranslations('vip');
  const router = useRouter();

  useVipUpgrade({
    subscriptionCallback: refetch,
    noLimitRoutePath: true
  });

  const { isTopLevel, isShowLevel, progress, startLabel, endLabel, userVipLevel } = useMemo(() => {
    // 满级
    const isTopLevel = vipDetail?.user_membership?.level !== 0 && vipDetail?.user_membership?.level === vipDetail?.user_membership?.next_membership?.level;

    // 0级（无积分）和满级不显示vip等级
    const isShowLevel = (vipDetail?.user_membership?.level === 0 && vipDetail?.user_points >= 0) ||
      vipDetail?.user_membership?.level !== 0;

    const progress = Math.round(vipDetail?.user_membership?.next_membership?.min_point ?
      (vipDetail?.user_points / vipDetail?.user_membership?.next_membership?.min_point) * 100 : 0);
    return {
      isTopLevel,
      isShowLevel,
      progress,
      startLabel: 'VIP' + vipDetail?.user_membership?.level,
      endLabel: 'VIP' + vipDetail?.user_membership?.next_membership?.level,
      userVipLevel: vipDetail?.user_membership?.level || 0
    };
  }, [vipDetail]);

  const themeData = useMemo(() => getVipCardThemeData(userVipLevel), [userVipLevel]);

  const goVipPage = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    track('profile_page_button_click', {
      current_page: 'Personal Center',
      button_name: 'Privileges',
    });
    onBeforeLinkJump?.();
    e.preventDefault();
    e.stopPropagation();
    router.push('/vip');
  }, [onBeforeLinkJump, router]);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    onVipDataLoad?.();
  }, [isLoading, onVipDataLoad]);

  if (isLoading) {
    return null;
  }

  return (
    <>
      {
        isShowLevel && (
          <div
            onClick={goVipPage}
            className={clsx("relative flex items-center w-full p-[5px] pr-[10px] rounded-[10px] click-effect-block aspect-[252/85]", className)}
            style={{
              backgroundImage: `url(${levelMap[userVipLevel || 1]?.cardBg})`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: '100% 100%',
              backgroundPosition: 'top left'
            }}
          >
            <div className='relative z-10 flex items-center w-full'>
              <div className='relative flex-initial w-[80px]'>
                {userVipLevel > 0 && <Image src={levelMap[userVipLevel]?.levelIcon} width={0} height={0} alt="vip" className='w-[80px]' />}
                {vipDetail && userVipLevel === 0 && <>
                  <Image src={vip1MaskImg} width={0} height={0} alt="" className='w-full' />
                  <div className='absolute left-0 bottom-0 overflow-hidden w-full'
                    style={{
                      height: `${Math.max(0, Math.min(progress, 100))}%`
                    }}>
                    <Image src={levelMap[userVipLevel]?.levelIcon} width={0} height={0} alt="vip" className='absolute left-0 bottom-0 w-[80px]' />
                  </div>
                </>}
              </div>
              <div
                className='flex flex-1 self-stretch flex-col justify-center pl-[5px]'
                style={{
                  marginLeft: userVipLevel === 0 ? '-5px' : '0'
                }}
              >
                <div className='mb-[12px] flex items-center justify-between'>
                  {!isTopLevel && userVipLevel <= 0 ? <span className='text-[13px] font-medium' style={{
                    color: themeData.color
                  }}>
                    {t('newVip.personalCard.noVipTitle')}
                  </span> :
                    <div
                      className='pr-[10px] text-[20px] italic leading-none bg-clip-text [-webkit-text-fill-color:transparent] font-bold'
                      style={{
                        backgroundImage: themeData.vipColor
                      }}
                    >
                      {startLabel}
                    </div>}
                  {isTopLevel && <div className='ml-[-5px] px-[6px] text-[#748094] text-[9px] font-medium bg-[rgba(0,0,0,0.3)] rounded-[5px]'>
                    {t("newVip.highestLevel")}
                  </div>}
                  <IconFont name="arrow-right" size={10} color={themeData.color} />
                </div>
                {!isTopLevel && <div className='mt-0'>
                  <div className="flex items-center justify-between leading-none mb-[5px]">
                    <div className="text-center font-medium text-[11px] text-[#A9B5CB]">
                      {toNumFixed(vipDetail?.user_points || 0)}/{vipDetail?.user_membership?.next_membership?.min_point}
                    </div>
                    <div className='text-[11px] font-medium text-[#A9B5CB]'>{endLabel}</div>
                  </div>
                  <Progress className='mb-[5px] h-[5px]' progress={progress} progressBarStyle={themeData.progressBarStyle} />
                </div>}
                {isTopLevel && <div className='text-[#A9B5CB] text-[10px] font-medium'>
                  {t('newVip.topLevelDesc', {
                    amount: toFormat(toNumFixed(vipDetail?.user_points || 0))
                  })}
                </div>}
              </div>
              {/* <IconFont name="arrow-right" size={10} className={clsx('absolute right-0', userVipLevel === 0 ? 'top-[17px]' : 'top-[20px]')} color={themeData.color} /> */}
            </div>
            <Link className='absolute top-0 -right-[1.25rem] bottom-0 -left-[1.25rem] z-20'
              href="/vip"
              onClick={onBeforeLinkJump} />
          </div >
        )
      }
    </>
  );
}